import {
  Card,
  CloseButton,
  CrudMode,
  Dialog,
  EmailComposer,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  Stack,
  Typography,
  buildPath,
  buildTemplatePath,
  handleClose,
  newToast,
  pn,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import { BulkSecondLevelValidation, BulkSelectedItemErrors, EmailModel } from "generated/openApi";
import produce from "immer";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";

const routeName = "studentEmail";

class Params {
  studentId: number = 0;
  screenType: ScreenType = "standard";
}

export const studentEmailPageRoute: PageRoute<Params> = {
  handle: "Email Student",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const StudentEmailPage = () => {
  const { params } = useParams(routeName, Params);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [emailModel, setEmailModel] = useState<EmailModel>({} as EmailModel);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [emailModel] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getEmail();
  }, []);

  const getEmail = () => {
    ApiService.students.getEmail(params.studentId).then((response) => {
      setEmailModel(response);
      resetUnsavedChanges([response]);
    });
  };

  const send = () => {
    ApiService.common.sendEmailsV2(emailModel).then(() => {
      dispatch(setAppToast(newToast("success")));
      navigate(`../`);
    });
  };

  const canSend = emailModel.recipients?.length && emailModel.subject && emailModel.body;

  const pageHeader = (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="inherit">{studentEmailPageRoute.handle}</Typography>

      <Stack direction="row" alignItems="center" spacing="sm">
        <CloseButton confirm={unsavedChanges} />

        <SubmitButton onClick={() => send()} disabled={!canSend} label="Send" />
      </Stack>
    </Stack>
  );

  const pageBody = (
    <Stack spacing="lg">
      <EmailComposer
        showRecipients
        recipients={emailModel?.recipients}
        setRecipients={(value) =>
          setEmailModel(
            produce(emailModel, (draft) => {
              draft.recipients = value;
            })
          )
        }
        readOnlyRecipients
        showSubject
        subject={emailModel?.subject}
        setSubject={(value) =>
          setEmailModel(
            produce(emailModel, (draft) => {
              draft.subject = value!;
            })
          )
        }
        showBody
        bodyHtml={emailModel?.body}
        setBodyHtml={(value) =>
          setEmailModel(
            produce(emailModel, (draft) => {
              draft.body = value!;
            })
          )
        }
        bodyHeight="300px"
      />
    </Stack>
  );

  return (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
